/**
 * Asset Card Component
 *
 * Displays a single media asset thumbnail in a square container.
 * Clicking the card selects/deselects the asset.
 */

import { useState, useEffect, useRef, useCallback, Ref, useMemo, memo } from 'react';
import type { IAsset } from '../providers';
import { ZprLabel, ZprTooltip } from '@zpr-stencil';
import { SmsIcon, ThumbUpIcon, AnimatedIcon, PenDotsIcon, StatusIcon } from '../../../icons';

import { useAssetActions } from '../context/AssetActionsContext';
import { useWorkflowStatuses } from '../hooks';
import { MediaType } from '../..';
import { FontPreview } from './FontPreview';

export interface AssetCardProps {
  /** The asset to display */
  asset: IAsset;
  /** Whether to show multi-select checkbox affordance */
  enableMultiSelect?: boolean;
  /** Whether the asset is selected */
  isSelected: boolean;
  /** Whether the asset is visually highlighted by an in-progress drag selection */
  isDragHighlighted?: boolean;
  /** Whether to show the asset title */
  showTitle?: boolean;
  /** Whether to show the asset status pill */
  showStatusPill?: boolean;
  /** Callback when the asset card is clicked */
  onClick: () => void;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  'thumbs_up': <ThumbUpIcon width={16} height={16} />,
  'sms': <SmsIcon width={16} height={16} />,
  'edit': <PenDotsIcon width={16} height={16} />
};

const TitleElement = memo(({ assetName, titleRef }: { assetName: string; titleRef: Ref<HTMLParagraphElement> | undefined }) => (
  <p
    ref={titleRef}
    className="asset-card__title"
  >
    {assetName}
  </p>
));


export const AssetCard = ({ asset, enableMultiSelect = false, isSelected, isDragHighlighted = false, showTitle = false, showStatusPill = false, onClick }: AssetCardProps) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string>('');
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const { zapworksProvider, workspaceId } = useAssetActions();
  const { statuses: workflowStatuses } = useWorkflowStatuses(zapworksProvider, workspaceId);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const assetName = useMemo(() => asset.getName(), [asset, asset.getName()]);
  const isFont = useMemo(() => asset.getMediaType() === MediaType.Font, [asset]);

  const thumbnailSizeConfig = { width: 256, height: 256 };

  const loadThumbnail = useCallback(async () => {
    const url = await asset.getImageUrlForCard(thumbnailSizeConfig);
    setThumbnailUrl(url);
  }, [asset]);

  // Only fetch the thumbnail URL once the card is visible in the viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          loadThumbnail();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [loadThumbnail]);

  // Check if title is truncated
  useEffect(() => {
    const checkTruncation = () => {
      if (titleRef.current) {
        const isTextTruncated = titleRef.current.scrollWidth > titleRef.current.clientWidth;
        setIsTruncated(isTextTruncated);
      }
    };

    checkTruncation();
    // Re-check on window resize
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [assetName]);

  const cardClasses = [
    'asset-card',
    isSelected && 'asset-card--selected',
    isDragHighlighted && 'asset-card--drag-highlighted',
  ].filter(Boolean).join(' ');

  const handleClick = () => {
    onClick();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  const metadata = asset.getMetadata();
  const workflowStatusUid = metadata.workflowStatusId as string | null;
  const workflowStatus = workflowStatuses.find(s => s.uid === workflowStatusUid) ?? null;
  const statusName = workflowStatus?.name;
  const statusIcon = workflowStatus?.iconKey ? STATUS_ICONS[workflowStatus.iconKey] || <StatusIcon width={16} height={16} /> : null;

  const isAnimated = metadata.animations && Array.isArray(metadata.animations) && metadata.animations.length > 0 || false;

  return (
    <div ref={containerRef} className="asset-card__container" data-asset-id={asset.getId()}>
      <div
        className={cardClasses}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label={`${enableMultiSelect ? (isSelected ? 'Deselect' : 'Select') : 'Open'} ${assetName}`}
      >
        {enableMultiSelect && (
          <span className="asset-card__checkbox" aria-hidden="true">
            <input type="checkbox" checked={isSelected} readOnly tabIndex={-1} />
          </span>
        )}
        {!isImageLoaded && !isFont && <div className="asset-card__shimmer" aria-hidden="true" />}
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt={assetName}
            onLoad={() => setIsImageLoaded(true)}
            draggable={false}
            loading="lazy"
            decoding="async"
            width={thumbnailSizeConfig.width}
            height={thumbnailSizeConfig.height}
          />
        )}
        {showStatusPill && statusIcon && (
          <div className="asset-card__status-pill">
            <ZprTooltip action="hover" placement="top">
              <span slot="trigger">{statusIcon}</span>
              <p slot="tooltip">{statusName}</p>
            </ZprTooltip>
          </div>
        )}
        {isAnimated && (
          <ZprLabel
            variant='badge'
            size='small'
            text='Animated'
            className="asset-card__animated-icon"
          >
              <span slot='icon'>
                <AnimatedIcon width={12} height={12} />
              </span>
          </ZprLabel>
        )}
        {isFont && (
          <FontPreview
            id={metadata.id as string || ''}
            family={assetName}
            isThumbnail={true}
          />
        )}
      </div>
      {showTitle && (
        isTruncated ? (
          <ZprTooltip action="hover" placement="top">
            <span slot="trigger"><TitleElement assetName={assetName} titleRef={titleRef} /></span>
            <p slot="tooltip">{assetName}</p>
          </ZprTooltip>
        ) : (
          <TitleElement assetName={assetName} titleRef={titleRef} />
        )
      )}
    </div>
  );
};
